import React, {useEffect, useState} from 'react';
import {Box, Typography} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import moment from 'moment';

import {apiGetDepotOrders} from 'constants/Api';
import {IFarmInputInvoice} from 'constants/interfaces';

import './OrderSummaryWidget.scss';

type StatusCount = {description: string; count: number};

type PriorityOrder = {
  id: number;
  farmerName: string;
  quantity: number;
  productName: string;
  totalCost: number;
  dueDate?: string;
  statusDescription: string;
};

export default function OrderSummaryWidget(): React.ReactElement {
  const [statusCounts, setStatusCounts] = useState<StatusCount[]>([]);
  const [priorityOrders, setPriorityOrders] = useState<PriorityOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    apiGetDepotOrders()
      .then((invoices: IFarmInputInvoice[]) => {
        if (!alive) return;

        const counts = new Map<string, number>();
        invoices.forEach((invoice) => {
          const description = invoice.currOrderStatus?.description || 'Unknown';
          counts.set(description, (counts.get(description) || 0) + 1);
        });

        const orders: PriorityOrder[] = invoices.flatMap((invoice) =>
          (invoice.orders || []).map((order) => ({
            id: order.id,
            farmerName: `${invoice.farmer?.name ?? ''} ${
              invoice.farmer?.surname ?? ''
            }`.trim(),
            quantity: order.quantity,
            productName: order.product?.name ?? '',
            totalCost: order.totalCost ?? order.price * order.quantity,
            dueDate: order.dueDate,
            statusDescription:
              invoice.currOrderStatus?.description || 'Unknown',
          })),
        );

        const sortedByDueDate = orders
          .filter((o) => !!o.dueDate)
          .sort(
            (a, b) =>
              new Date(a.dueDate as string).getTime() -
              new Date(b.dueDate as string).getTime(),
          )
          .slice(0, 5);

        setStatusCounts(
          Array.from(counts.entries()).map(([description, count]) => ({
            description,
            count,
          })),
        );
        setPriorityOrders(sortedByDueDate);
      })
      .catch((err) => {
        console.error('Failed to load order summary for widget', err);
        if (alive) setUnavailable(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  if (loading) {
    return <Skeleton variant="rect" height={360} style={{borderRadius: 8}} />;
  }

  if (unavailable) {
    return (
      <Box className="order-summary-widget">
        <Typography className="widget-heading">ORDER SUMMARY</Typography>
        <Typography variant="body2" color="textSecondary">
          Could not load order data.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="order-summary-widget">
      <Typography className="widget-heading">ORDER SUMMARY</Typography>

      {statusCounts.length > 0 ? (
        <Box className="status-counts">
          {statusCounts.map((sc, idx) => (
            <Box key={idx} className="status-badge">
              <Typography className="count">{sc.count}</Typography>
              <Typography className="description">{sc.description}</Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="textSecondary">
          No orders found.
        </Typography>
      )}

      {priorityOrders.length > 0 && (
        <Box className="priority-orders-section">
          <Typography className="section-title">Top Priority Orders</Typography>
          <Box className="priority-list">
            {priorityOrders.map((order) => (
              <Box key={order.id} className="priority-card">
                <Box className="row-top">
                  <Typography className="farmer-name">
                    {order.farmerName}
                  </Typography>
                  <Typography className="order-status">
                    {order.statusDescription}
                  </Typography>
                </Box>
                <Typography className="row-mid">
                  {order.quantity}x {order.productName}
                </Typography>
                <Box className="row-bottom">
                  <Typography className="cost">
                    {formatCurrency(order.totalCost)}
                  </Typography>
                  <Typography className="due-date">
                    {order.dueDate
                      ? moment(order.dueDate).format('DD MMM YYYY')
                      : '-'}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
